import React, { useState, useEffect } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import api from '../services/api';
import useWorkspaceBasePath from '../hooks/useWorkspaceBasePath';

const BusinessSuppliers = () => {
  const outletContext = useOutletContext();
  const canManage = outletContext?.canManageDebts ?? true;
  const navigate = useNavigate();
  const routeBase = useWorkspaceBasePath();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [showModal, setShowModal] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [showRestockModal, setShowRestockModal] = useState(false);
  const [restockHistory, setRestockHistory] = useState([]);
  const [loadingRestocks, setLoadingRestocks] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showPaymentHistory, setShowPaymentHistory] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    montant: '', mode_paiement: 'especes', reference: '', notes: '', date_paiement: new Date().toISOString().split('T')[0]
  });
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [formData, setFormData] = useState({
    nom: '', telephone: '', email: '', adresse: '', notes: ''
  });

  useEffect(() => {
    loadSuppliers();
  }, []);

  const loadSuppliers = async () => {
    try {
      const res = await api.get('/business/suppliers');
      setSuppliers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedSupplier) {
        await api.put(`/business/suppliers/${selectedSupplier.id}`, formData);
      } else {
        await api.post('/business/suppliers', formData);
      }
      loadSuppliers();
      closeModal();
    } catch (err) {
      console.error(err);
      alert('Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce fournisseur ?')) return;
    try {
      await api.delete(`/business/suppliers/${id}`);
      loadSuppliers();
    } catch (err) {
      console.error(err);
    }
  };

  const openModal = (supplier = null) => {
    if (supplier) {
      setSelectedSupplier(supplier);
      setFormData({
        nom: supplier.nom || '',
        telephone: supplier.telephone || '',
        email: supplier.email || '',
        adresse: supplier.adresse || '',
        notes: supplier.notes || ''
      });
    } else {
      setSelectedSupplier(null);
      setFormData({ nom: '', telephone: '', email: '', adresse: '', notes: '' });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedSupplier(null);
  };

  const openRestockHistory = async (supplier) => {
    setSelectedSupplier(supplier);
    setShowRestockModal(true);
    setLoadingRestocks(true);
    try {
      const res = await api.get(`/business/suppliers/${supplier.id}/restocks`);
      setRestockHistory(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
      setRestockHistory([]);
    } finally {
      setLoadingRestocks(false);
    }
  };

  const openPaymentModal = (supplier) => {
    setSelectedSupplier(supplier);
    setPaymentForm({
      montant: parseFloat(supplier.solde_credit) || '',
      mode_paiement: 'especes',
      reference: '',
      notes: '',
      date_paiement: new Date().toISOString().split('T')[0]
    });
    setShowPaymentModal(true);
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    if (!selectedSupplier) return;
    setPaymentProcessing(true);
    try {
      await api.post(`/business/suppliers/${selectedSupplier.id}/payments`, paymentForm);
      setShowPaymentModal(false);
      loadSuppliers();
      alert('✅ Paiement enregistré avec succès');
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || 'Erreur lors du paiement');
    } finally {
      setPaymentProcessing(false);
    }
  };

  const openPaymentHistory = async (supplier) => {
    setSelectedSupplier(supplier);
    setShowPaymentHistory(true);
    setLoadingPayments(true);
    try {
      const res = await api.get(`/business/suppliers/${supplier.id}/payments`);
      setPaymentHistory(Array.isArray(res.data?.payments) ? res.data.payments : (Array.isArray(res.data) ? res.data : []));
    } catch (err) {
      console.error(err);
      setPaymentHistory([]);
    } finally {
      setLoadingPayments(false);
    }
  };

  const filteredSuppliers = suppliers.filter(s =>
    s.nom?.toLowerCase().includes(search.toLowerCase()) ||
    s.telephone?.includes(search) ||
    s.email?.toLowerCase().includes(search.toLowerCase())
  );

  // Pagination
  const totalPages = Math.ceil(filteredSuppliers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedSuppliers = filteredSuppliers.slice(startIndex, startIndex + itemsPerPage);

  // Stats
  const stats = {
    total: suppliers.length,
    withRestocks: suppliers.filter(s => parseInt(s.total_ravitaillements) > 0).length,
    totalMontant: suppliers.reduce((sum, s) => sum + (parseFloat(s.total_montant_ravitaillements) || 0), 0),
    totalRestocks: suppliers.reduce((sum, s) => sum + (parseInt(s.total_ravitaillements) || 0), 0),
    totalCredit: suppliers.reduce((sum, s) => sum + (parseFloat(s.solde_credit) || 0), 0),
    withCredit: suppliers.filter(s => parseFloat(s.solde_credit) > 0).length
  };

  if (loading) {
    return <div className="p-8 flex justify-center"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div></div>;
  }

  return (
    <div className="p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-800">Fournisseurs</h1>
          <p className="text-gray-500 text-sm">Gérez vos fournisseurs et leurs ravitaillements</p>
        </div>
        {canManage && (
          <button
            onClick={() => openModal()}
            className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition flex items-center justify-center gap-2"
          >
            <span>➕</span> Nouveau fournisseur
          </button>
        )}
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl shadow p-4 mb-6">
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">🔍</span>
          <input
            type="text"
            placeholder="Rechercher un fournisseur (nom, téléphone, email)..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
            className="w-full pl-10 pr-4 py-2 border rounded-xl focus:ring-2 focus:ring-indigo-400 focus:outline-none"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Résultat de recherche */}
      {search && (
        <div className="mb-4 text-sm text-gray-500">
          {filteredSuppliers.length} résultat(s) pour "{search}"
        </div>
      )}

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 md:gap-4 mb-6">
        <div className="bg-white rounded-xl shadow p-3 md:p-4">
          <div className="text-blue-500 text-xl md:text-2xl mb-1 md:mb-2">🏭</div>
          <div className="text-xl md:text-2xl font-bold text-gray-800">{stats.total}</div>
          <div className="text-xs md:text-sm text-gray-500">Total fournisseurs</div>
        </div>
        <div className="bg-white rounded-xl shadow p-3 md:p-4">
          <div className="text-green-500 text-xl md:text-2xl mb-1 md:mb-2">🚚</div>
          <div className="text-xl md:text-2xl font-bold text-green-600">{stats.totalRestocks}</div>
          <div className="text-xs md:text-sm text-gray-500">Ravitaillements</div>
        </div>
        <div className="bg-white rounded-xl shadow p-3 md:p-4">
          <div className="text-orange-500 text-xl md:text-2xl mb-1 md:mb-2">📦</div>
          <div className="text-xl md:text-2xl font-bold text-orange-600">{stats.withRestocks}</div>
          <div className="text-xs md:text-sm text-gray-500">Actifs</div>
        </div>
        <div className="bg-white rounded-xl shadow p-3 md:p-4">
          <div className="text-amber-500 text-xl md:text-2xl mb-1 md:mb-2">💰</div>
          <div className="text-lg md:text-2xl font-bold text-amber-600">
            <span className="hidden sm:inline">{stats.totalMontant.toLocaleString()}</span>
            <span className="sm:hidden">{(stats.totalMontant / 1000).toFixed(0)}k</span>
            <span className="text-xs md:text-sm"> XOF</span>
          </div>
          <div className="text-xs md:text-sm text-gray-500">Total achats</div>
        </div>
        <div className={`rounded-xl shadow p-3 md:p-4 ${stats.totalCredit > 0 ? 'bg-red-50 border border-red-200' : 'bg-white'}`}>
          <div className="text-red-500 text-xl md:text-2xl mb-1 md:mb-2">📝</div>
          <div className={`text-lg md:text-2xl font-bold ${stats.totalCredit > 0 ? 'text-red-600' : 'text-gray-400'}`}>
            <span className="hidden sm:inline">{stats.totalCredit.toLocaleString()}</span>
            <span className="sm:hidden">{(stats.totalCredit / 1000).toFixed(0)}k</span>
            <span className="text-xs md:text-sm"> XOF</span>
          </div>
          <div className="text-xs md:text-sm text-gray-500">{stats.withCredit} crédit(s) en cours</div>
        </div>
      </div>

      {/* Suppliers List */}
      {paginatedSuppliers.length > 0 ? (
        <>
          {/* Desktop Table */}
          <div className="hidden md:block bg-white rounded-xl shadow overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Fournisseur</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Contact</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Ravitaillements</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Total achats</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Crédit dû</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {paginatedSuppliers.map(supplier => (
                  <tr key={supplier.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-800">{supplier.nom}</div>
                      {supplier.adresse && <div className="text-xs text-gray-400">{supplier.adresse}</div>}
                    </td>
                    <td className="px-4 py-3">
                      {supplier.telephone && <div className="text-sm text-gray-600">📞 {supplier.telephone}</div>}
                      {supplier.email && <div className="text-sm text-gray-500">✉️ {supplier.email}</div>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={`font-bold ${parseInt(supplier.total_ravitaillements) > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                        {supplier.total_ravitaillements || 0}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={`font-bold ${parseFloat(supplier.total_montant_ravitaillements) > 0 ? 'text-amber-600' : 'text-gray-400'}`}>
                        {(parseFloat(supplier.total_montant_ravitaillements) || 0).toLocaleString()} XOF
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {parseFloat(supplier.solde_credit) > 0 ? (
                        <span className="font-bold text-red-600">
                          {parseFloat(supplier.solde_credit).toLocaleString()} XOF
                        </span>
                      ) : (
                        <span className="text-gray-400 text-sm">0 XOF</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-center gap-1">
                        <button
                          onClick={() => openRestockHistory(supplier)}
                          className="p-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition"
                          title="Ravitaillements"
                        >
                          🚚
                        </button>
                        {parseFloat(supplier.solde_credit) > 0 && canManage && (
                          <button
                            onClick={() => openPaymentModal(supplier)}
                            className="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition"
                            title="Payer le crédit"
                          >
                            💵
                          </button>
                        )}
                        <button
                          onClick={() => openPaymentHistory(supplier)}
                          className="p-2 bg-purple-50 text-purple-600 rounded-lg hover:bg-purple-100 transition"
                          title="Historique paiements"
                        >
                          📋
                        </button>
                        {canManage && (
                          <>
                            <button
                              onClick={() => openModal(supplier)}
                              className="p-2 bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 transition"
                              title="Modifier"
                            >
                              ✏️
                            </button>
                            <button
                              onClick={() => handleDelete(supplier.id)}
                              className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition"
                              title="Supprimer"
                            >
                              🗑️
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-3">
            {paginatedSuppliers.map(supplier => (
              <div key={supplier.id} className="bg-white rounded-xl shadow p-4">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <div className="font-medium text-gray-800">{supplier.nom}</div>
                    {supplier.telephone && <div className="text-sm text-gray-500">📞 {supplier.telephone}</div>}
                    {supplier.email && <div className="text-xs text-gray-400 truncate max-w-[180px]">✉️ {supplier.email}</div>}
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-bold text-green-600 block">
                      {supplier.total_ravitaillements || 0} ravit.
                    </span>
                    {parseFloat(supplier.total_montant_ravitaillements) > 0 && (
                      <span className="text-xs font-semibold text-amber-600 block mt-0.5">
                        {parseFloat(supplier.total_montant_ravitaillements).toLocaleString()} XOF
                      </span>
                    )}
                  </div>
                </div>
                {parseFloat(supplier.solde_credit) > 0 && (
                  <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded-lg flex justify-between items-center">
                    <span className="text-sm text-red-700">📝 Crédit dû</span>
                    <span className="font-bold text-red-600">{parseFloat(supplier.solde_credit).toLocaleString()} XOF</span>
                  </div>
                )}
                <div className="flex justify-between items-center border-t pt-3">
                  <div className="flex gap-2">
                    {parseFloat(supplier.solde_credit) > 0 && canManage && (
                      <button
                        onClick={() => openPaymentModal(supplier)}
                        className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg text-sm font-medium"
                      >
                        💵 Payer
                      </button>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => openRestockHistory(supplier)}
                      className="p-2 bg-green-50 text-green-600 rounded-lg"
                    >
                      🚚
                    </button>
                    <button
                      onClick={() => openPaymentHistory(supplier)}
                      className="p-2 bg-purple-50 text-purple-600 rounded-lg"
                    >
                      📋
                    </button>
                    {canManage && (
                      <>
                        <button
                          onClick={() => openModal(supplier)}
                          className="p-2 bg-indigo-50 text-indigo-600 rounded-lg"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDelete(supplier.id)}
                          className="p-2 bg-red-50 text-red-600 rounded-lg"
                        >
                          🗑️
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="bg-white rounded-xl shadow p-12 text-center">
          <span className="text-6xl mb-4 block">🏭</span>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            {search ? 'Aucun résultat' : 'Aucun fournisseur'}
          </h3>
          <p className="text-gray-500 mb-4">
            {search ? `Aucun fournisseur ne correspond à "${search}"` : 'Commencez par ajouter vos fournisseurs'}
          </p>
          {!search && canManage && (
            <button
              onClick={() => openModal()}
              className="px-6 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition"
            >
              Ajouter un fournisseur
            </button>
          )}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-2 mt-6">
          <button
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
            className={`px-3 py-2 rounded-lg ${currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50 shadow'}`}
          >
            ««
          </button>
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className={`px-4 py-2 rounded-lg ${currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50 shadow'}`}
          >
            ← Précédent
          </button>
          
          <div className="flex gap-1 items-center">
            {(() => {
              const pages = [];
              const showPages = new Set();
              showPages.add(1);
              showPages.add(totalPages);
              for (let i = Math.max(1, currentPage - 1); i <= Math.min(totalPages, currentPage + 1); i++) {
                showPages.add(i);
              }
              const sortedPages = [...showPages].sort((a, b) => a - b);
              sortedPages.forEach((page, idx) => {
                if (idx > 0 && page - sortedPages[idx - 1] > 1) {
                  pages.push(<span key={`ellipsis-${page}`} className="px-2 text-gray-400">...</span>);
                }
                pages.push(
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`w-10 h-10 rounded-lg ${currentPage === page ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50 shadow'}`}
                  >
                    {page}
                  </button>
                );
              });
              return pages;
            })()}
          </div>
          
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className={`px-4 py-2 rounded-lg ${currentPage === totalPages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50 shadow'}`}
          >
            Suivant →
          </button>
          <button
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages}
            className={`px-3 py-2 rounded-lg ${currentPage === totalPages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50 shadow'}`}
          >
            »»
          </button>
        </div>
      )}

      {/* Modal Fournisseur */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-lg my-auto max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold text-gray-800">
                {selectedSupplier ? 'Modifier le fournisseur' : 'Nouveau fournisseur'}
              </h2>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
                <input
                  type="text"
                  value={formData.nom}
                  onChange={(e) => setFormData({...formData, nom: e.target.value})}
                  required
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                  <input
                    type="tel"
                    value={formData.telephone}
                    onChange={(e) => setFormData({...formData, telephone: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
                <input
                  type="text"
                  value={formData.adresse}
                  onChange={(e) => setFormData({...formData, adresse: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  rows={2}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="flex-1 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition"
                >
                  {selectedSupplier ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Historique Ravitaillements */}
      {showRestockModal && selectedSupplier && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-2xl my-auto max-h-[90vh] flex flex-col">
            <div className="p-6 border-b flex-shrink-0">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-gray-800">Ravitaillements</h2>
                  <p className="text-gray-500">{selectedSupplier.nom}</p>
                </div>
                <button onClick={() => setShowRestockModal(false)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
              </div>
              <div className="mt-3 p-3 bg-green-50 rounded-lg flex gap-4 flex-wrap">
                <div>
                  <div className="text-sm text-gray-600">Total ravitaillements</div>
                  <div className="text-2xl font-bold text-green-600">{selectedSupplier.total_ravitaillements || 0}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Montant total</div>
                  <div className="text-2xl font-bold text-amber-600">
                    {(parseFloat(selectedSupplier.total_montant_ravitaillements) || 0).toLocaleString()} XOF
                  </div>
                </div>
                {parseFloat(selectedSupplier.solde_credit) > 0 && (
                  <div>
                    <div className="text-sm text-gray-600">Crédit en cours</div>
                    <div className="text-2xl font-bold text-red-600">
                      {parseFloat(selectedSupplier.solde_credit).toLocaleString()} XOF
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="p-6 flex-1 overflow-y-auto">
              {loadingRestocks ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-600"></div>
                </div>
              ) : restockHistory.length > 0 ? (
                <div className="space-y-3">
                  {restockHistory.map(restock => (
                    <div
                      key={restock.id}
                      onClick={() => { setShowRestockModal(false); navigate(`${routeBase}/restock/history?id=${restock.id}`); }}
                      className="border rounded-xl p-4 hover:bg-purple-50 transition cursor-pointer group"
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-medium text-gray-800 group-hover:text-purple-700 transition">
                            {restock.reference}
                            <span className="ml-2 text-xs text-purple-500 opacity-0 group-hover:opacity-100 transition">Voir le bon →</span>
                          </div>
                          <div className="text-sm text-gray-500">
                            📅 {new Date(restock.date_ravitaillement).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-amber-600">{parseFloat(restock.cout_total).toLocaleString()} XOF</div>
                          <div className="text-xs text-gray-500">
                            {restock.total_produits} produit(s) • {restock.total_unites} unité(s)
                          </div>
                          {restock.statut_paiement && restock.statut_paiement !== 'paye' && (
                            <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${restock.statut_paiement === 'credit' ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'}`}>
                              {restock.statut_paiement === 'credit' ? '📝 À crédit' : `💳 Partiel (${parseFloat(restock.montant_paye || 0).toLocaleString()} XOF payé)`}
                            </span>
                          )}
                        </div>
                      </div>
                      {restock.notes && (
                        <div className="mt-2 text-sm text-gray-500 italic">
                          {restock.notes}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <span className="text-4xl block mb-3">🚚</span>
                  <p className="text-gray-500">Aucun ravitaillement enregistré pour ce fournisseur</p>
                  <button
                    onClick={() => { setShowRestockModal(false); navigate(`${routeBase}/restock`); }}
                    className="mt-3 px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition text-sm"
                  >
                    Faire un ravitaillement
                  </button>
                </div>
              )}
            </div>

            <div className="p-4 border-t flex justify-end">
              <button
                onClick={() => setShowRestockModal(false)}
                className="px-4 py-2 bg-gray-100 text-gray-600 rounded-xl hover:bg-gray-200 transition"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Paiement Fournisseur */}
      {showPaymentModal && selectedSupplier && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-lg my-auto max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold text-gray-800">💵 Payer le fournisseur</h2>
              <p className="text-gray-500">{selectedSupplier.nom}</p>
              <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-red-700">Crédit actuel</span>
                  <span className="text-xl font-bold text-red-600">{parseFloat(selectedSupplier.solde_credit).toLocaleString()} XOF</span>
                </div>
              </div>
            </div>
            <form onSubmit={handlePayment} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Montant à payer *</label>
                <div className="relative">
                  <input
                    type="number"
                    value={paymentForm.montant}
                    onChange={(e) => setPaymentForm({...paymentForm, montant: e.target.value})}
                    required
                    min="1"
                    max={parseFloat(selectedSupplier.solde_credit)}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 focus:outline-none pr-14"
                    placeholder="Montant"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">XOF</span>
                </div>
                <div className="flex gap-2 mt-1">
                  <button type="button" onClick={() => setPaymentForm({...paymentForm, montant: Math.round(parseFloat(selectedSupplier.solde_credit) / 4)})} className="text-xs text-blue-600 hover:underline">25%</button>
                  <button type="button" onClick={() => setPaymentForm({...paymentForm, montant: Math.round(parseFloat(selectedSupplier.solde_credit) / 2)})} className="text-xs text-blue-600 hover:underline">50%</button>
                  <button type="button" onClick={() => setPaymentForm({...paymentForm, montant: Math.round(parseFloat(selectedSupplier.solde_credit) * 0.75)})} className="text-xs text-blue-600 hover:underline">75%</button>
                  <button type="button" onClick={() => setPaymentForm({...paymentForm, montant: parseFloat(selectedSupplier.solde_credit)})} className="text-xs text-blue-600 hover:underline">Tout</button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mode de paiement</label>
                  <select
                    value={paymentForm.mode_paiement}
                    onChange={(e) => setPaymentForm({...paymentForm, mode_paiement: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 focus:outline-none"
                  >
                    <option value="especes">Espèces</option>
                    <option value="mobile_money">Mobile Money</option>
                    <option value="virement">Virement</option>
                    <option value="cheque">Chèque</option>
                    <option value="autre">Autre</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                  <input
                    type="date"
                    value={paymentForm.date_paiement}
                    onChange={(e) => setPaymentForm({...paymentForm, date_paiement: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Référence</label>
                <input
                  type="text"
                  value={paymentForm.reference}
                  onChange={(e) => setPaymentForm({...paymentForm, reference: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 focus:outline-none"
                  placeholder="N° reçu, référence transaction..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm({...paymentForm, notes: e.target.value})}
                  rows={2}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 focus:outline-none"
                  placeholder="Notes optionnelles..."
                />
              </div>

              {paymentForm.montant && parseFloat(paymentForm.montant) < parseFloat(selectedSupplier.solde_credit) && (
                <div className="p-2 bg-orange-50 border border-orange-200 rounded-lg text-xs text-orange-700">
                  Après ce paiement, il restera <strong>{(parseFloat(selectedSupplier.solde_credit) - parseFloat(paymentForm.montant)).toLocaleString()} XOF</strong> de crédit.
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowPaymentModal(false)}
                  className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={paymentProcessing}
                  className="flex-1 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {paymentProcessing ? (
                    <><div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div> En cours...</>
                  ) : (
                    '💵 Valider le paiement'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Historique Paiements */}
      {showPaymentHistory && selectedSupplier && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-2xl my-auto max-h-[90vh] flex flex-col">
            <div className="p-6 border-b flex-shrink-0">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-gray-800">📋 Historique des paiements</h2>
                  <p className="text-gray-500">{selectedSupplier.nom}</p>
                </div>
                <button onClick={() => setShowPaymentHistory(false)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
              </div>
              {parseFloat(selectedSupplier.solde_credit) > 0 && (
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex justify-between items-center">
                  <span className="text-sm text-red-700">Crédit restant</span>
                  <span className="text-xl font-bold text-red-600">{parseFloat(selectedSupplier.solde_credit).toLocaleString()} XOF</span>
                </div>
              )}
            </div>

            <div className="p-6 flex-1 overflow-y-auto">
              {loadingPayments ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
                </div>
              ) : paymentHistory.length > 0 ? (
                <div className="space-y-3">
                  {paymentHistory.map(payment => (
                    <div key={payment.id} className="border rounded-xl p-4 hover:bg-gray-50 transition">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-medium text-gray-800">
                            💵 {parseFloat(payment.montant).toLocaleString()} XOF
                          </div>
                          <div className="text-sm text-gray-500">
                            📅 {new Date(payment.date_paiement).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                          </div>
                          {payment.reference && <div className="text-xs text-gray-400 mt-1">Réf: {payment.reference}</div>}
                        </div>
                        <div className="text-right">
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                            {payment.mode_paiement === 'especes' ? '💵 Espèces' : 
                             payment.mode_paiement === 'mobile_money' ? '📱 Mobile Money' :
                             payment.mode_paiement === 'virement' ? '🏦 Virement' :
                             payment.mode_paiement === 'cheque' ? '📄 Chèque' : '🔄 Autre'}
                          </span>
                        </div>
                      </div>
                      {payment.notes && (
                        <div className="mt-2 text-sm text-gray-500 italic">{payment.notes}</div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <span className="text-4xl block mb-3">📋</span>
                  <p className="text-gray-500">Aucun paiement enregistré pour ce fournisseur</p>
                </div>
              )}
            </div>

            <div className="p-4 border-t flex justify-between">
              {parseFloat(selectedSupplier.solde_credit) > 0 && canManage && (
                <button
                  onClick={() => { setShowPaymentHistory(false); openPaymentModal(selectedSupplier); }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition text-sm"
                >
                  💵 Faire un paiement
                </button>
              )}
              <button
                onClick={() => setShowPaymentHistory(false)}
                className="px-4 py-2 bg-gray-100 text-gray-600 rounded-xl hover:bg-gray-200 transition ml-auto"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessSuppliers;
